import * as Sentry from '@sentry/react';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;
/**
 * Prefixed ids (WorkOS `org_01HQ…`, Temporal `sendEmailWorkflow-019f…`). The digit is
 * what separates them from hyphenated route names like `email-workflow-progress`.
 */
const PREFIXED_ID_SEGMENT = /^[A-Za-z][A-Za-z0-9]*[_-][A-Za-z0-9_-]*\d[A-Za-z0-9_-]*$/;
/** Long, digit-bearing and unbroken: an identifier rather than a route name. */
const OPAQUE_SEGMENT = /^(?=.*\d)[A-Za-z0-9]{8,}$/;

export interface TransportErrorContext {
	method: HTTP_METHOD;
	url: string;
	requestId?: string;
	durationMs: number;
}

/**
 * `fetch` rejects with a TypeError when the request never produced an HTTP
 * response — connection closed/reset, DNS, TLS, blocked preflight. TypeError
 * alone is the marker: the API layer's other failure modes are a thrown
 * `Response` (4xx/5xx), a DOMException (AbortError) and plain Errors, none of
 * which are TypeErrors. Deliberately avoids referencing `Response` so the
 * predicate stays usable where that global is absent.
 */
export const isTransportError = (error: unknown): boolean => error instanceof TypeError;

/**
 * Query string dropped and id-shaped segments collapsed, so repeat failures on one
 * endpoint group into a single Sentry issue instead of one per deal.
 *
 * Collapsing has to be generous: this string is used as a Sentry fingerprint and
 * title, and some routes interpolate user data straight into the path — most sharply
 * `/validation/iban/{iban}`, which would otherwise put a customer's bank account
 * number into Sentry's grouping key and open one issue per IBAN typed.
 */
export const toEndpointShape = (url: string): string => {
	const [path] = url.split('?');
	return path
		.split('/')
		.map((segment) => (isIdentifierSegment(segment) ? ':id' : segment))
		.join('/');
};

const isIdentifierSegment = (segment: string): boolean =>
	UUID_SEGMENT.test(segment) ||
	NUMERIC_SEGMENT.test(segment) ||
	PREFIXED_ID_SEGMENT.test(segment) ||
	OPAQUE_SEGMENT.test(segment);

/**
 * Records a transport-level API failure. Without this these are invisible
 * everywhere: there is no status code for Sentry's httpClientIntegration to
 * report on, the request never reaches the backend so nothing is logged there,
 * and the API layer catches the rejection so no unhandled rejection fires.
 */
const REPORTED_TO_SENTRY = Symbol('REPORTED_TO_SENTRY');

/**
 * Whether `reportTransportError` already sent this throwable to Sentry. Callers that
 * suppress duplicates must ask this rather than re-testing `isTransportError`: the
 * reporter only wraps the fetch itself, so a TypeError raised elsewhere in the API
 * layer — `createUrl` on an unpopulated config, `response.json()` on a mid-stream
 * disconnect — looks identical but was never reported.
 */
export const wasReportedToSentry = (error: unknown): boolean =>
	typeof error === 'object' && error !== null && REPORTED_TO_SENTRY in error;

export const reportTransportError = (error: unknown, context: TransportErrorContext): void => {
	if (!isTransportError(error)) {
		return;
	}
	Object.defineProperty(error, REPORTED_TO_SENTRY, { value: true, enumerable: false });

	const endpoint = toEndpointShape(context.url);
	const detail = {
		endpoint,
		method: context.method,
		requestId: context.requestId,
		durationMs: Math.round(context.durationMs),
		online: navigator.onLine,
	};

	logger.error('API transport failure', detail);

	Sentry.captureException(error, {
		tags: { api_failure: 'transport', api_method: context.method },
		extra: detail,
		fingerprint: ['api-transport-error', context.method, endpoint],
	});
};
